import { useQueryClient } from '@tanstack/react-query';

import { useDelivery } from '~/src/common/hooks/useDelivery';
import { useApiEcomClientError } from '~/src/common/services/error-handler/error-handler';
import I18n from '~/src/common/services/I18n';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import Toaster from '~/src/common/services/Toaster';
import Tracker from '~/src/common/services/Tracker';
import { Cart } from '~/src/common/typings/cart';
import { getCartQuantity } from '~/src/common/utils/cart/cart';
import {
  getGetCartQueryKey,
  useAddProducts,
  useGetCart,
} from '~/src/queries/api-ecom/generated/api-ecom';
import { AddProductsBodyProductsItemSource } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import { ApiEcomError } from '~/src/queries/services/client';
import { useATCSourceFrom } from '~/src/screens/CatalogContent/ContentScreen/hooks/useATCSourceFrom';
import { useRecipeScreenAnalyticsContext } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/context';
import {
  ClientRecipeIngredientProduct,
  RecipeIngredientProductArticle,
} from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/types';
import { getAvailableArticlesFromIngredients } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/utils/getAvailableArticlesFromIngredients';

interface Props {
  ingredients: ClientRecipeIngredientProduct[];
  onSuccess?: (articles: RecipeIngredientProductArticle[], cart?: Cart) => void;
  servings: number;
}

const useAddAllArticles = ({ ingredients, onSuccess, servings }: Props) => {
  const { recipe, categoryName, subcategoryName } = useRecipeScreenAnalyticsContext();
  const handleError = useApiEcomClientError();
  const queryClient = useQueryClient();

  const { data: cart } = useGetCart();

  const { mutateAsync: updateProducts, isLoading } = useAddProducts({
    mutation: {
      onSuccess: async () => {
        const availableArticles = getAvailableArticlesFromIngredients(ingredients, cart);

        const nbArticles = availableArticles.reduce((acc, p) => acc + p.quantity, 0);

        Toaster.notify({
          icon: 'basket-normal',
          message: I18n.t('recipe-screen.add-products-success-message', {
            count: nbArticles,
          }),
        });

        await queryClient.invalidateQueries(getGetCartQueryKey());

        // Mise à jour de la quantité des ingrédients du formulaire qui peut être minorée
        // si des produits manquent de stock
        onSuccess?.(availableArticles, cart);
      },
      onError: (error: unknown) => {
        handleError(error as ApiEcomError);
      },
    },
  });

  const { isSetup: isDeliverySetup, isTimeSlotValid, address } = useDelivery();
  const isAddingAllowed = isDeliverySetup && isTimeSlotValid;
  const deliveryAddressModal = useNiceModal('delivery-address-modal');
  const deliverySelectSlotModal = useNiceModal('delivery-select-slot-modal');

  const atcSourceFrom = useATCSourceFrom();

  const addAllArticles = async (location: 'ingredients list' | 'principal ATC') => {
    if (!isAddingAllowed) {
      if (address) {
        void deliverySelectSlotModal.show();
      } else {
        void deliveryAddressModal.show();
      }

      return;
    }

    const availableArticles = getAvailableArticlesFromIngredients(ingredients, cart);

    const payload = availableArticles.map(a => {
      const originalArticle = ingredients.find(i => i.article?.id === a.id)?.originalArticle;

      return {
        id: a.id,
        sku: a.sku,
        quantity: a.quantity + getCartQuantity(cart, a),
        source: {
          type: 'recipe ingredients list',
          detail: {
            replacedSku: originalArticle?.sku,
            recipeId: recipe?.id ?? '',
            from: atcSourceFrom.type ? atcSourceFrom : undefined,
          },
        } as AddProductsBodyProductsItemSource,
      };
    });

    await updateProducts({ data: { products: payload } });

    const nbArticles = availableArticles.reduce((acc, p) => acc + p.quantity, 0);
    const total = availableArticles.reduce((acc, p) => acc + (p.itemPrice ?? 0), 0);

    Tracker.sendEvent('add ingredients to cart', {
      'click location': location,
      'recipe category': categoryName ?? '',
      'recipe subcategory': subcategoryName,
      'recipe id': recipe?.id ?? '',
      'recipe name': recipe?.name ?? '',
      'number of articles': nbArticles,
      'recipe price': total / 100,
      'recipe event source': 'recipe ingredients list',
      'portion quantity': servings,
    });
  };

  return {
    addAllArticles,
    isLoading,
  };
};

export default useAddAllArticles;
